import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useOktaAuth } from '@okta/okta-react'

import {
	Form,
	SpaceBetween,
	Button,
	Header,
	Container,
	Modal,
	Box,
	Badge
} from '@cloudscape-design/components'

import FormInput from '../../../components/ControlledFormInputs/Input'
import RaciRoleSelector from '../../../components/AccessManagement/RaciRoleSelector'

import { useNewAccessManagementFormStore } from '../../../stores/access-management/newAccessManagementFormStore'
import type { CreateAccessRequest } from '../../../types/access'
import useCreatePermissionsForUser from '../../../hooks/mutate/useCreatePermissionsForUser'
import useUserPermissions from '../../../hooks/query/useUserPermissions'
import {
	buildPermissionsPayload,
	isSuperAdmin
} from '../../../helper/permissionRoles'
import { validateNewAccessEmail } from '../../../helper/validateNewAccessEmail'
import { showAccessManagementNotification } from '../../../helper/accessManagementFlashbar'
import { useFlashbarStateStore } from '../../../stores/notifications/flashbarStateStore'

const GLOBE_EMAIL_PATTERN = /^[^\s@]+@(globe|gsupport)\.com\.ph$/

const NewAccessManagementForm: FC = () => {
	const navigate = useNavigate()
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string
	const { data: currentUserAccess } = useUserPermissions(thisUserEmail)

	const {
		formValues,
		actions: { setFormValues, setFormValidity }
	} = useNewAccessManagementFormStore()

	const {
		control,
		trigger,
		watch,
		setError,
		clearErrors,
		formState: { isValid }
	} = useForm<{
		email: string
		raciRoles: Array<{ label: string; value: string }>
		customPermissions: Array<{ label: string; dismissLabel: string }>
	}>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: {
			email: '',
			raciRoles: [],
			customPermissions: []
		}
	})

	const emailValue = watch('email')
	const [isCheckingEmail, setIsCheckingEmail] = useState(false)
	const [submitModalVisible, setSubmitModalVisible] = useState(false)

	useEffect(() => {
		let cancelled = false
		const trimmedEmail = emailValue?.trim() || ''

		if (!trimmedEmail || !GLOBE_EMAIL_PATTERN.test(trimmedEmail)) {
			setIsCheckingEmail(false)
			return
		}

		setIsCheckingEmail(true)

		const timer = setTimeout(async () => {
			try {
				const result = await validateNewAccessEmail(trimmedEmail)
				if (cancelled) {
					return
				}

				if (result === true) {
					clearErrors('email')
					await trigger('email')
				} else {
					setError('email', { type: 'validate', message: result })
				}
			} catch {
				if (!cancelled) {
					setError('email', {
						type: 'validate',
						message: 'Unable to validate user at this time. Please try again.'
					})
				}
			} finally {
				if (!cancelled) {
					setIsCheckingEmail(false)
				}
			}
		}, 500)

		return () => {
			cancelled = true
			clearTimeout(timer)
		}
	}, [clearErrors, emailValue, setError, trigger])

	useEffect(() => {
		setFormValidity(isValid && !isCheckingEmail)
	}, [isCheckingEmail, isValid, setFormValidity])

	const { mutate: createPermissionsForUser, isLoading } =
		useCreatePermissionsForUser()
	const flashbarActions = useFlashbarStateStore()

	const handleSubmitClick = async () => {
		const trimmedEmail = formValues.email?.trim() || ''
		if (trimmedEmail && GLOBE_EMAIL_PATTERN.test(trimmedEmail)) {
			const emailCheck = await validateNewAccessEmail(trimmedEmail)
			if (emailCheck !== true) {
				setError('email', { type: 'validate', message: emailCheck })
				return
			}
		}

		const valid = await trigger()
		if (!valid) return

		const permissions = buildPermissionsPayload(
			formValues.raciRoles,
			formValues.customPermissions
		)

		if (permissions.length === 0) {
			return
		}

		setSubmitModalVisible(true)
	}

	const handleSubmitConfirm = () => {
		const permissions = buildPermissionsPayload(
			formValues.raciRoles,
			formValues.customPermissions
		)

		const createAccessRequest: CreateAccessRequest = {
			userEmail: formValues.email,
			permissions,
			action: 'CREATE'
		}

		createPermissionsForUser(createAccessRequest, {
			onSuccess: () => {
				setSubmitModalVisible(false)
				showAccessManagementNotification(flashbarActions, {
					type: 'success',
					header: 'Access created',
					content: `SSP access was granted to ${formValues.email}.`
				})
				navigate('/access-management')
			}
		})
	}

	return (
		<>
			<Form
				actions={
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="link" onClick={() => navigate(-1)}>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleSubmitClick}
							loading={isLoading}
							disabled={!isValid || isCheckingEmail}
						>
							Submit
						</Button>
					</SpaceBetween>
				}
				header={
					<Header variant="h1" description="">
						Add New Access
					</Header>
				}
			>
				<Container>
					<SpaceBetween direction="vertical" size="l">
						<FormInput
							name="email"
							label="Email"
							type="email"
							control={control}
							rules={{
								required:
									'Please enter the email address of the user you want to add',
								pattern: {
									value: GLOBE_EMAIL_PATTERN,
									message:
										'Please enter a valid Globe email address. Example: name@globe.com.ph or name@gsupport.com.ph'
								}
							}}
							setFormValues={setFormValues}
						/>

						<RaciRoleSelector
							control={control}
							setFormValues={setFormValues}
							raciRoles={formValues.raciRoles}
							customPermissions={formValues.customPermissions}
							showCustomPermissions={isSuperAdmin(
								currentUserAccess?.permissions
							)}
						/>
					</SpaceBetween>
				</Container>
			</Form>

			<Modal
				visible={submitModalVisible}
				onDismiss={() => !isLoading && setSubmitModalVisible(false)}
				header="Confirm new access"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								disabled={isLoading}
								onClick={() => setSubmitModalVisible(false)}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								loading={isLoading}
								onClick={handleSubmitConfirm}
							>
								Confirm
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween size="s">
					<Box>
						Grant SSP access to <strong>{formValues.email}</strong> with the
						selected RACI roles?
					</Box>
					{formValues.raciRoles.length > 0 && (
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '6px'
							}}
						>
							{(formValues.raciRoles as Array<{ label: string; value: string }>).map(
								(role) => (
									<Badge key={role.value} color="green">
										{role.label}
									</Badge>
								)
							)}
						</div>
					)}
				</SpaceBetween>
			</Modal>
		</>
	)
}

export default NewAccessManagementForm
